import axios from 'axios'
import {
  CSRF_HEADER,
  clearCsrfToken,
  ensureCsrfToken,
  requiresCsrfToken,
} from './csrf'

const API_URL = import.meta.env.VITE_API_URL || '/api/v1'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // The CSRF token must be attached here, on the `api` instance. Setting
  // `axios.defaults.headers.common` on the global axios object does not reach
  // this instance — `axios.create()` snapshots the defaults it was built with.
  if (requiresCsrfToken(config.method, config.url)) {
    const csrfToken = await ensureCsrfToken()
    if (csrfToken) {
      config.headers[CSRF_HEADER] = csrfToken
    }
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // CSRF tokens expire after an hour. On rejection, fetch a fresh one and
    // replay the request once so the expiry is invisible to the user.
    if (error.response?.status === 403 && error.config && !error.config._csrfRetry) {
      const detail = String(error.response?.data ?? '')
      if (detail.includes('CSRF')) {
        clearCsrfToken()
        const retryConfig = { ...error.config, _csrfRetry: true }
        const freshToken = await ensureCsrfToken()
        if (freshToken) {
          retryConfig.headers = { ...retryConfig.headers, [CSRF_HEADER]: freshToken }
          return api.request(retryConfig)
        }
      }
    }

    if (error.response?.status === 401) {
      // Don't redirect on CSRF token requests - these might fail before auth is set up
      const isCSRFRequest = error.config?.url?.includes('csrf-token')
      const isAuthCheck = error.config?.url?.includes('/auth/me')
      const isPublicAuth =
        error.config?.url?.includes('/auth/login') ||
        error.config?.url?.includes('/auth/register') ||
        error.config?.url?.includes('/auth/verify-email') ||
        error.config?.url?.includes('/auth/resend-verification')

      if (!isCSRFRequest && !isAuthCheck && !isPublicAuth) {
        localStorage.removeItem('token')
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)